import { useEffect, useRef, useState } from "react";

/**
 * Hook to detect when an element enters the viewport
 * Returns [ref, isVisible] - attach ref to element you want to observe
 */
export function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once revealed, stop observing (animation plays once)
          observer.unobserve(element);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || "0px 0px -50px 0px",
      }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
}

/**
 * Component wrapper for scroll reveal animations
 */
export function ScrollReveal({ 
  children, 
  animation = "reveal-up", 
  delay = 0,
  className = "",
  as: Component = "div"
}) {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Component
      ref={ref}
      className={`${className} ${isVisible ? `${animation} reveal-delay-${delay}` : "opacity-0"}`}
      style={{ 
        opacity: isVisible ? undefined : 0,
        transition: "opacity 0.1s"
      }}
    >
      {children}
    </Component>
  );
}

export default useScrollReveal;
